import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowRight, Waves } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { AdminAgentOverview, AdminAgentInvoice } from "../../../shared/admin-agent";

const money = (cents: number) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD",
}).format(cents / 100);

const date = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

async function fetchOverview(): Promise<AdminAgentOverview> {
  const response = await fetch("/api/admin/agent-overview", {
    credentials: "include", headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Agent overview unavailable (HTTP ${response.status}).`);
  return response.json();
}

function InvoiceAttention({ invoice }: { invoice: AdminAgentInvoice }) {
  return (
    <div className="flex flex-col gap-3 border-t py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <Link href={`/admin/users/${encodeURIComponent(invoice.userId)}`} className="font-medium text-sm hover:underline break-all">
          Member {invoice.userId}
        </Link>
        <p className="text-xs text-muted-foreground">
          {invoice.status} · {invoice.replies.toLocaleString()} replies · created {date(invoice.createdAt)}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="text-sm font-semibold tabular-nums">{money(invoice.amountCents)}</span>
        <Link href={`/admin/users/${encodeURIComponent(invoice.userId)}#sms-diagnostics`}
          className="text-xs text-primary underline underline-offset-2">SMS diagnostics</Link>
      </div>
    </div>
  );
}

export default function AdminAgentOverview() {
  const query = useQuery<AdminAgentOverview, Error>({
    queryKey: ["/api/admin/agent-overview"],
    queryFn: fetchOverview,
    refetchInterval: 60000,
    retry: 1,
  });

  return (
    <section className="space-y-4" aria-label="Agent membership overview">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 dark:text-teal-300">Membership operations</p>
          <h2 className="mt-1 flex items-center gap-2 text-xl font-semibold"><Waves className="h-5 w-5 text-teal-600" /> Agent access</h2>
        </div>
        <Link href="/admin?view=users" className="text-sm text-primary underline underline-offset-4">Browse members <ArrowRight className="inline h-3.5 w-3.5" /></Link>
      </div>
      {query.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map(item => <Skeleton key={item} className="h-24 rounded-xl" />)}
        </div>
      ) : query.error || !query.data ? (
        <div role="alert" className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-start gap-3 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 text-destructive" />
            <div className="flex-1"><p className="font-medium">Could not load Agent overview</p><p className="mt-1 text-muted-foreground">{query.error?.message ?? "No overview data was returned."}</p></div>
            <Button size="sm" variant="outline" onClick={() => void query.refetch()}>Retry</Button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            Current membership counts · usage and invoice totals for {date(query.data.periodStart)}–{date(query.data.periodEnd)} (UTC).
            Effective tiers are exclusive; access-source counts can overlap.
          </p>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            {[
              ["Agent members", query.data.counts.agent, "Effective tier"],
              ["Pro members", query.data.counts.pro, "Effective tier"],
              ["Free members", query.data.counts.free, "Effective tier"],
              ["Paid Agent", query.data.counts.paidAgent, "Access source"],
              ["Paid Pro", query.data.counts.paidPro, "Access source"],
              ["Complimentary Agent", query.data.counts.complimentaryAgent, "Access source"],
              ["Complimentary Pro", query.data.counts.complimentaryPro, "Access source"],
              ["Test access", query.data.counts.test, "Access source"],
            ].map(([label, value, note]) => (
              <Card key={String(label)} className="border-teal-900/10 bg-gradient-to-br from-teal-50/70 to-card dark:from-teal-950/20">
                <CardContent className="p-4">
                  <p className="text-2xl font-semibold tabular-nums">{Number(value).toLocaleString()}</p>
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-[11px] text-muted-foreground">{note}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-[1fr_1.15fr]">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm">Agent activity · current UTC month</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-x-5 gap-y-4 text-sm">
                <Metric label="Included replies used" value={query.data.usage.replies} />
                <Metric label="Automated alert texts" value={query.data.usage.automatedTexts} />
                <Metric label="Automated segments" value={query.data.usage.automatedSegments} />
                <Metric label="Pending replies" value={query.data.usage.pending} />
                <Metric label="Uncertain replies" value={query.data.usage.uncertain} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Reply charges · USD</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Accrued: uninvoiced replies reserved in this UTC month, including uncertain sends;
                  not final charges. Pending and paid: invoices created this UTC month.
                  Paid status is provider-confirmed and retained locally; not a live reconciliation.
                  Unresolved: all ages, not additive to pending. These are not revenue or profit.
                </p>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-x-5 gap-y-4 text-sm">
                <Metric label="Accrued" value={money(query.data.invoices.accruedCents)} />
                <Metric label="Pending" value={money(query.data.invoices.pendingCents)} />
                <Metric label="Confirmed paid" value={money(query.data.invoices.paidCents)} />
                <Metric label="Unresolved" value={`${query.data.invoices.unresolvedCount} · ${money(query.data.invoices.unresolvedCents)}`} />
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>Accounts needing attention</span>
                <span className="text-xs font-normal text-muted-foreground">Unresolved invoices, all ages · bounded list</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {query.data.attention.length === 0 ? (
                <div className="rounded-lg border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">No unresolved invoice accounts in the attention window.</div>
              ) : query.data.attention.map(invoice => <InvoiceAttention key={invoice.id} invoice={invoice} />)}
            </CardContent>
          </Card>
        </>
      )}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return <div><p className="text-lg font-semibold tabular-nums">{typeof value === "number" ? value.toLocaleString() : value}</p><p className="text-xs text-muted-foreground">{label}</p></div>;
}
